import { isEmpty } from 'lodash';
import { Platform } from 'react-native';

import { QuestionType } from '@/components/inputs/QuestionGroup';
import { getFileExtension, getFileExtensionFromUri } from '@/utils/getFileExtension';
import { safeJsonParse } from '@/utils/jsonParse';

import { client } from '..';
import {
  NonProfitOrg,
  NonProfitOrgProfileResponse,
  NonProfitOrgResponse,
  NonProfitOrgUpdateParams,
  NPOLightRatingQuestionAnswers,
  OCRResponseData,
  PaymentMethodResponse,
  NonProfitOrgQrResponse,
  SearchByNameParams,
  SearchByNameSuggestion
} from '../models/nonProfitOrgModels';

export const getNonProfitOrgs = async ({
  queryKey,
  pageParam
}: {
  queryKey: (string | string[] | number | boolean | undefined)[];
  pageParam: string | undefined;
}): Promise<NonProfitOrgResponse> => {
  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    __,
    radio,
    religions,
    type,
    filterByDistance,
    latitude,
    longitude,
    limit,
    denominations,
    programs,
    claimed,
    sort_light_rating
  ] = queryKey;

  const parsedPageParam = safeJsonParse<{ n: string; lastLightRaitingScore: string }>(pageParam ?? '');

  const selectedReligions = new URLSearchParams({ religions: religions?.toString() ?? '' }).toString();
  const selectedDenominations = new URLSearchParams({ denominations: denominations?.toString() ?? '' }).toString();
  const selectedPromgras = new URLSearchParams({ programs: programs?.toString() ?? '' }).toString();
  const orderByParam = new URLSearchParams({
    orderBy: JSON.stringify({
      lightRaitingScore: sort_light_rating === 'htl' ? 'DESC' : 'ASC'
    })
  }).toString();
  const radioParam = `${radio ? `&radio=${radio}` : ''}`;
  const typeParam = `${type ? `&type=${type}` : ''}`;
  const latitudeParam = `${latitude ? `&latitude=${latitude}` : ''}`;
  const longitudeParam = `${longitude ? `&longitude=${longitude}` : ''}`;
  const claimedParam = `${claimed ? `&claimed=${claimed}` : ''}`;
  const religionsParam = `${!isEmpty(selectedReligions) ? selectedReligions : ''}`;
  const denominationsParams = `${!isEmpty(selectedDenominations) ? selectedDenominations : ''}`;
  const programsParams = `${!isEmpty(selectedPromgras) ? selectedPromgras : ''}`;

  const url = `/non-profit-orgs/scrolling?filterByDistance=${filterByDistance ?? false}${radioParam}${typeParam}&limit=${limit}${parsedPageParam && parsedPageParam?.n && parsedPageParam?.lastLightRaitingScore ? `&n=${parsedPageParam.n}&lastLightRaiting=${parsedPageParam.lastLightRaitingScore}` : ''}&${religionsParam}&${denominationsParams}&${programsParams}&${orderByParam}${latitudeParam}${longitudeParam}${claimedParam}`;

  const response = await client.get(url);

  return response.data;
};

export const getNonProfitOrgById = async ({ id }: { id: string }): Promise<NonProfitOrg> => {
  const url = `non-profit-orgs/${id}`;
  const { data } = await client.get(url);

  return data[0];
};

export const getNonProfitOrgInsights = async ({ id }: { id: string }): Promise<NonProfitOrgProfileResponse> => {
  const url = `non-profit-orgs/insight/church/${id}`;
  const { data } = await client.get(url);

  return data[0];
};

export const getLightRatingQuestions = async (): Promise<QuestionType[]> => {
  const url = `onboaring-questionary`;
  const { data } = await client.get(url);

  return data;
};

export const getNPOLightRatingQuestionsAnswers = async ({
  id
}: {
  id: string;
}): Promise<NPOLightRatingQuestionAnswers[]> => {
  const url = `onboaring-questionary/npo/${id}`;
  const { data } = await client.get(url);

  return data;
};

export const updateNonProfitOrg = async ({
  npoId,
  name,
  location, // GeoPoint
  email,
  address,
  type, // NonProfitOrgType
  thumbnail, // Filepath array
  claimed,
  description,
  plStatement, // Filepath array
  balanceSheet, // Filepath array
  title,
  subtitile,
  revenue,
  totalExpenses,
  employees,
  totalEmployeeBeneficits,
  volunteers,
  totalSalaries,
  claimedUserId,
  thirdPartyAudit, // Filepath array
  thirdPartyAuditDeny,
  impactStudy, // Filepath array
  impactStudyDeny,
  byLaws, // Filepath array
  showMap,
  totalLandBuildingEquiment,
  totalFundraisingMarketing,
  totalEmployees,
  totalVolunteers,
  totalGrantsPrograms,
  nineNintyForm // Filepath array
}: NonProfitOrgUpdateParams): Promise<NonProfitOrg> => {
  const url = `/non-profit-orgs/${npoId}`;

  const { data } = await client.patch(url, {
    name,
    location,
    email,
    address,
    type,
    thumbnail,
    claimed,
    description,
    plStatement,
    balanceSheet,
    title,
    subtitile,
    revenue,
    totalExpenses,
    employees,
    totalEmployeeBeneficits,
    volunteers,
    totalSalaries,
    claimedUserId,
    thirdPartyAudit,
    thirdPartyAuditDeny,
    impactStudy,
    impactStudyDeny,
    byLaws,
    showMap,
    totalLandBuildingEquiment,
    totalFundraisingMarketing,
    totalEmployees,
    totalVolunteers,
    totalGrantsPrograms,
    nineNintyForm
  });

  return data;
};

export const updateNonProfitOrgWiki = async ({
  npoId,
  description,
  plStatement, // Filepath array
  revenue,
  thirdPartyAudit, // Filepath array
  thirdPartyAuditDeny,
  salaries,
  usersBoardDirectory,
  pastors
}: NonProfitOrgUpdateParams): Promise<NonProfitOrg> => {
  const url = `/non-profit-orgs/${npoId}/wiki`;

  const { data } = await client.patch(url, {
    description,
    plStatement,
    revenue,
    thirdPartyAudit,
    thirdPartyAuditDeny,
    salaries,
    boardDirectories: usersBoardDirectory,
    pastors
  });

  return data;
};

export const saveWikiTentativeData = async ({
  npoId,
  email,
  phone
}: {
  npoId: string;
  email: string;
  phone: string;
}): Promise<any> => {
  const url = `/non-profit-orgs/${npoId}/tentative`;

  const { data } = await client.post(url, {
    email,
    phone
  });

  return data;
};

export const updateLightRatingQuestions = async ({
  npoId,
  answers
}: {
  npoId: string;
  answers: { questionId: string; answer: string | boolean | null }[];
}): Promise<NonProfitOrg> => {
  const url = `/onboaring-questionary`;

  const { data } = await client.patch(url, {
    npoId,
    answers
  });

  return data;
};

export const searchNpoByName = async ({
  name,
  claimed,
  canReceiveDonations,
  limit
}: SearchByNameParams): Promise<SearchByNameSuggestion[]> => {
  const url = `/non-profit-orgs/search?name=${name}${claimed !== undefined ? `&claimed=${claimed}` : ''}${canReceiveDonations !== undefined ? `&canReceiveDonations=${canReceiveDonations}` : ''}${limit !== undefined ? `&limit=${limit}` : ''}`;
  const { data } = await client.get(url);

  return data;
};

export const searchNpoAISearch = async ({
  query,
  limit
}: {
  query: string;
  limit?: number;
}): Promise<SearchByNameSuggestion[]> => {
  const url = `/non-profit-orgs/iaSearch?query=${query}${limit !== undefined ? `&limit=${limit}` : ''}`;
  const { data } = await client.get(url);

  return data;
};

export const claimNpoValidation = async ({
  nonProfitOrgId,
  firstName,
  lastName,
  email,
  phone
}: {
  nonProfitOrgId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}): Promise<{ validationId: string }> => {
  const { data } = await client.post('/users/claim-validation', {
    nonProfitOrgId,
    firstName,
    lastName,
    email,
    phone
  });

  return data;
};

export const claimNpoSendEmailWithFiles = async ({
  body,
  nonProfitOrgId,
  driverLicense,
  articlesOfIncorporation
}: {
  body: { validationId: string; validationCode: string };
  nonProfitOrgId: string;
  driverLicense: string | File;
  articlesOfIncorporation: string | File;
}): Promise<any> => {
  const formData = new FormData();
  if (Platform.OS === 'web') {
    const driverLicenseExtension = getFileExtension(driverLicense as File);
    formData.append(
      'driversLicense',
      driverLicense as File,
      `driversLicense_${nonProfitOrgId}.${!isEmpty(driverLicenseExtension) ? driverLicenseExtension : 'pdf'}`
    );
    if (articlesOfIncorporation) {
      const articlesOfIncorporationExtension = getFileExtension(articlesOfIncorporation as File);
      formData.append(
        'articleOfIncorporation',
        articlesOfIncorporation as File,
        `articleOfIncorporation_${nonProfitOrgId}.${!isEmpty(articlesOfIncorporationExtension) ? articlesOfIncorporationExtension : 'pdf'}`
      );
    }
  } else {
    if (driverLicense) {
      const driverLicenseExtension = getFileExtensionFromUri(driverLicense as string);
      formData.append('driversLicense', {
        uri: driverLicense,
        type: 'application/pdf',
        name: `driver_license_${nonProfitOrgId}.${!isEmpty(driverLicenseExtension) ? driverLicenseExtension : 'pdf'}`
      } as any);
    }
    if (articlesOfIncorporation) {
      const articlesOfIncorporationExtension = getFileExtensionFromUri(articlesOfIncorporation as string);
      formData.append('articleOfIncorporation', {
        uri: articlesOfIncorporation,
        type: 'application/pdf',
        name: `articleOfIncorporation_${nonProfitOrgId}.${!isEmpty(articlesOfIncorporationExtension) ? articlesOfIncorporationExtension : 'pdf'}`
      } as any);
    }
  }

  formData.append('data', JSON.stringify(body));

  const { data } = await client.post(`/non-profit-orgs/claim`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return data;
};

// 990 OCR Upload
export const nineNintyOcrUpload = async ({
  files,
  nonProfitOrgId
}: {
  files: string | File;
  nonProfitOrgId: string;
}): Promise<OCRResponseData> => {
  const formData = new FormData();
  if (Platform.OS === 'web') {
    formData.append('files', files as File, `nine_ninty_form_${nonProfitOrgId}.pdf`);
  } else {
    formData.append('files', {
      uri: files,
      type: 'application/pdf',
      name: `nine_ninty_form_${nonProfitOrgId}.pdf`
    } as any);
  }
  formData.append('nonProfitOrgId', nonProfitOrgId);

  const { data } = await client.post('/ocr/990', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  return data;
};

// Get payment methods availables
export const getNonProfitPaymentMethods = async ({ id }: { id: string }): Promise<PaymentMethodResponse> => {
  const url = `/non-profit-orgs/payment-methods/${id}`;
  const { data } = await client.get(url);

  return data;
};

export const nonProfitOrgQrCode = async ({
  appsflyerBody
}: {
  appsflyerBody: string;
}): Promise<NonProfitOrgQrResponse> => {
  const { data } = await client.post(`/non-profit-orgs/generate/qr`, {
    appsflyerBody
  });

  return data;
};
